import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';

const SiteWiseReportPage = () => {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [handovers, setHandovers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [assetsRes, handoversRes] = await Promise.all([
          api.getAssets({ limit: 1000 }),
          api.getHandovers({ limit: 1000 }),
        ]);

        if (assetsRes.success) setAssets(assetsRes.data || []);
        if (handoversRes.success) setHandovers(handoversRes.data || []);
      } catch (error) {
        console.error('Error loading site-wise report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const rows = useMemo(() => {
    const totalAssets = assets.length || 1;
    const siteMap = new Map();

    assets.forEach((asset) => {
      const siteName = asset.location?.site?.name || 'Unassigned';
      if (!siteMap.has(siteName)) {
        siteMap.set(siteName, {
          siteName,
          total: 0,
          available: 0,
          assigned: 0,
          issues: 0,
        });
      }
      const entry = siteMap.get(siteName);
      entry.total += 1;
      if (asset.status === 'available') entry.available += 1;
      else if (asset.status === 'issue_reported') entry.issues += 1;
      else entry.assigned += 1;
    });

    // Issues and pending handovers per site
    const pendingMap = new Map();
    handovers.forEach((h) => {
      const siteName =
        h.asset?.location?.site?.name ||
        assets.find((a) => (a._id || a.id) === (h.asset?._id || h.asset?.id || h.asset))
          ?.location?.site?.name ||
        'Unassigned';

      if (h.acknowledgementStatus === 'pending') {
        pendingMap.set(siteName, (pendingMap.get(siteName) || 0) + 1);
      }

      if (h.acknowledgementStatus === 'issue_reported' || h.issues) {
        const entry = siteMap.get(siteName) || {
          siteName,
          total: 0,
          available: 0,
          assigned: 0,
          issues: 0,
        };
        entry.issues += 1;
        siteMap.set(siteName, entry);
      }
    });

    return Array.from(siteMap.values())
      .map((entry) => ({
        ...entry,
        pendingHandovers: pendingMap.get(entry.siteName) || 0,
        percent: Math.round((entry.total / totalAssets) * 100),
      }))
      .sort((a, b) => b.total - a.total);
  }, [assets, handovers]);

  if (loading && assets.length === 0 && handovers.length === 0) {
    return <Loader />;
  }

  return (
    <div className="site-wise-report-page" style={{ padding: '16px' }}>
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <div style={{ fontSize: '14px', color: '#718096' }}>
            Overview of assets and handovers grouped by site.
          </div>
          <div style={{ fontSize: '12px', color: '#a0aec0' }}>
            Total sites: {rows.length}
          </div>
        </div>

        {rows.length === 0 ? (
          <p style={{ fontSize: '13px', color: '#a0aec0', margin: 0 }}>No data available</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
                minWidth: '700px',
              }}
            >
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '8px 6px',
                      color: '#718096',
                      fontSize: '11px',
                    }}
                  >
                    Site
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 6px',
                      color: '#718096',
                      fontSize: '11px',
                    }}
                  >
                    Total Assets
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 6px',
                      color: '#718096',
                      fontSize: '11px',
                    }}
                  >
                    Available
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 6px',
                      color: '#718096',
                      fontSize: '11px',
                    }}
                  >
                    Assigned
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 6px',
                      color: '#718096',
                      fontSize: '11px',
                    }}
                  >
                    Issues
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 6px',
                      color: '#718096',
                      fontSize: '11px',
                    }}
                  >
                    Pending Handovers
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 6px',
                      color: '#718096',
                      fontSize: '11px',
                    }}
                  >
                    % of Assets
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.siteName} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 6px', color: '#2d3748' }}>{row.siteName}</td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', color: '#2d3748' }}>
                      {row.total}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', color: '#38a169' }}>
                      {row.available}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', color: '#3182ce' }}>
                      {row.assigned}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', color: '#e53e3e' }}>
                      {row.issues}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', color: '#ed8936' }}>
                      {row.pendingHandovers}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', color: '#4a5568' }}>
                      {row.percent}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteWiseReportPage;

